import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Menu, X, User } from "lucide-react";
import { useCart } from "../../context/CartContext";

const NAV = [
  { to: "/shop", label: "Shop" },
  { to: "/care", label: "Care Library" },
  { to: "/journal", label: "Journal" },
  { to: "/about", label: "About" },
  { to: "/visit", label: "Visit" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count, openCart } = useCart();
  const location = useLocation();

  // Pages with a full-bleed hero want a transparent nav at the very top.
  const overHero = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const solid = scrolled || !overHero || open;

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 260, damping: 28 }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        solid ? "bg-canvas/90 shadow-sm backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="container-fp flex items-center justify-between py-4">
        <Link to="/" className="flex items-baseline gap-1.5">
          <span
            className={`font-display text-2xl font-medium ${
              solid ? "text-forest-700" : "text-canvas"
            }`}
          >
            Fancy
          </span>
          <span className={`font-display text-2xl italic ${solid ? "text-brass" : "text-brass"}`}>
            Plants
          </span>
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  solid ? "text-ink hover:text-forest-700" : "text-canvas/90 hover:text-canvas"
                } ${isActive ? "!text-brass" : ""}`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <Link
            to="/account"
            aria-label="Account"
            className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
              solid ? "text-ink hover:bg-forest-100" : "text-canvas hover:bg-canvas/10"
            }`}
          >
            <User size={20} />
          </Link>
          <CartButton solid={solid} count={count} onClick={openCart} />
        </div>

        <div className="flex items-center gap-1 md:hidden">
          <CartButton solid={solid} count={count} onClick={openCart} />
          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className={`p-2 ${solid ? "text-ink" : "text-canvas"}`}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="border-t border-line bg-canvas md:hidden"
        >
          <div className="container-fp flex flex-col py-2">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `py-3 text-base font-medium ${isActive ? "text-brass" : "text-ink"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/account"
              className={({ isActive }) =>
                `flex items-center gap-2 py-3 text-base font-medium ${isActive ? "text-brass" : "text-ink"}`
              }
            >
              <User size={18} /> Account
            </NavLink>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}

function CartButton({ solid, count, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Cart, ${count} items`}
      className={`relative flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
        solid ? "text-ink hover:bg-forest-100" : "text-canvas hover:bg-canvas/10"
      }`}
    >
      <ShoppingBag size={20} />
      {count > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-brass text-[11px] font-semibold text-canvas-raised">
          {count}
        </span>
      )}
    </button>
  );
}
